import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card } from '../../components/ui/card';
import { toast } from 'sonner';
import { PercentSquare, Users as UsersIcon, Calendar, TrendingDown, Check, X, AlertTriangle } from 'lucide-react';

/**
 * Director Dashboard — Staff Discount Report
 *
 * Single source of truth for every manual discount applied via the
 * Bar Panel quick-discount pills (`POST /api/orders/{id}/discount`,
 * kind ∈ {cash_10, friends_20}). Pulls from `/api/analytics/discounts`
 * which already returns:
 *   • totals (count + $ across both kinds)
 *   • by_bartender leaderboard
 *   • by_day daily series
 *   • recent (last 50 events)
 *
 * Defaults to the current trading day. Owner can shift the window to
 * a custom date range (start_date / end_date) for weekly / monthly
 * pay-period reports.
 */
function getISODate(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

export default function DiscountReportPage() {
  const [startDate, setStartDate] = useState(getISODate(-6)); // last 7 days incl today
  const [endDate, setEndDate] = useState(getISODate(0));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/analytics/discounts', {
        params: { start_date: startDate, end_date: endDate },
      });
      setData(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const approve = async (orderId) => {
    setBusy(orderId);
    try {
      await api.post(`/orders/${orderId}/discount/approve`);
      toast.success('Discount approved');
      fetchReport();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Approve failed');
    } finally { setBusy(''); }
  };

  const reject = async (orderId) => {
    const reason = window.prompt('Reason for rejecting (optional):', '');
    if (reason === null) return;
    setBusy(orderId);
    try {
      await api.post(`/orders/${orderId}/discount/reject`, { reason });
      toast.success('Discount rejected — order is back to full price');
      fetchReport();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reject failed');
    } finally { setBusy(''); }
  };

  const applyPreset = (preset) => {
    if (preset === 'today') {
      setStartDate(getISODate(0));
      setEndDate(getISODate(0));
    } else if (preset === 'week') {
      setStartDate(getISODate(-6));
      setEndDate(getISODate(0));
    } else if (preset === 'month') {
      setStartDate(getISODate(-29));
      setEndDate(getISODate(0));
    }
  };

  const totals = data?.totals || { count: 0, amount: 0, by_kind: {} };
  const cash10 = totals.by_kind?.cash_10 || { count: 0, amount: 0 };
  const friends20 = totals.by_kind?.friends_20 || { count: 0, amount: 0 };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="discount-report-page">
      {/* Date range controls */}
      <div className="flex items-end gap-3 flex-wrap bg-white/[0.03] border border-white/10 rounded-sm p-4">
        <div>
          <label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-sans block mb-1">Start</label>
          <Input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="bg-white/[0.04] border-white/10 text-white font-mono h-9 w-44"
            data-testid="discount-start-date"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-sans block mb-1">End</label>
          <Input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="bg-white/[0.04] border-white/10 text-white font-mono h-9 w-44"
            data-testid="discount-end-date"
          />
        </div>
        <div className="flex items-center gap-1.5">
          <PresetBtn label="Today" onClick={() => applyPreset('today')} testId="preset-today" />
          <PresetBtn label="Last 7 days" onClick={() => applyPreset('week')} testId="preset-week" />
          <PresetBtn label="Last 30 days" onClick={() => applyPreset('month')} testId="preset-month" />
        </div>
        <Button
          onClick={fetchReport}
          disabled={loading}
          className="h-9 bg-gold/15 text-gold hover:bg-gold/25 border border-gold/40 rounded-sm font-sans text-xs uppercase tracking-[0.15em] font-semibold ml-auto"
          data-testid="discount-refresh-btn"
        >
          {loading ? 'Loading…' : 'Refresh'}
        </Button>
      </div>

      {/* Pending Approvals — top priority, always visible if any */}
      {data?.pending?.length > 0 && (
        <Card className="bg-amber-500/[0.06] border-amber-500/30 rounded-sm p-4" data-testid="discount-pending-card">
          <h3 className="text-sm font-serif text-amber-300 uppercase tracking-[0.18em] mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Pending approval ({data.pending.length})
          </h3>
          <p className="text-[11px] text-amber-200/70 font-sans mb-3">
            Bartender-applied 20% discounts. The customer is still being charged full price until you approve.
          </p>
          <div className="space-y-2">
            {data.pending.map(p => (
              <div
                key={p.order_id}
                className="flex items-center justify-between gap-3 py-2.5 px-3 bg-white/[0.03] rounded-sm border border-amber-500/20 text-sm font-sans flex-wrap"
                data-testid={`pending-${p.order_id}`}
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className="text-gold font-serif text-sm font-semibold">T{p.table_number}</span>
                  <span className="text-white truncate">{p.customer_name || 'guest'}</span>
                  <Badge className="border-0 bg-amber-500/15 text-amber-300 font-sans text-[10px] uppercase tracking-[0.12em] animate-pulse">
                    {p.discount_label?.replace(' (Pending Approval)', '') || '20% Off'}
                  </Badge>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-zinc-500">by <span className="text-zinc-200">{p.applied_by_name}</span></span>
                  <span className="text-zinc-500 font-mono">
                    ${p.subtotal.toFixed(2)} − <span className="text-red-300">${p.discount_amount.toFixed(2)}</span>
                  </span>
                  <Button
                    size="sm"
                    onClick={() => approve(p.order_id)}
                    disabled={busy === p.order_id}
                    className="h-8 bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 border border-emerald-500/40 rounded-sm font-sans text-[11px] uppercase tracking-[0.12em] font-semibold disabled:opacity-50"
                    data-testid={`pending-approve-${p.order_id}`}
                  >
                    <Check className="w-3.5 h-3.5 mr-1" /> Approve
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => reject(p.order_id)}
                    disabled={busy === p.order_id}
                    className="h-8 bg-red-500/15 text-red-300 hover:bg-red-500/25 border border-red-500/30 rounded-sm font-sans text-[11px] uppercase tracking-[0.12em] font-semibold disabled:opacity-50"
                    data-testid={`pending-reject-${p.order_id}`}
                  >
                    <X className="w-3.5 h-3.5 mr-1" /> Reject
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile
          label="Total discounted"
          value={`$${totals.amount?.toFixed(2) ?? '0.00'}`}
          sub={`${totals.count} order${totals.count === 1 ? '' : 's'}`}
          accent="gold"
          icon={TrendingDown}
          testId="stat-total"
        />
        <StatTile
          label="10% off"
          value={`$${cash10.amount?.toFixed(2) ?? '0.00'}`}
          sub={`${cash10.count} time${cash10.count === 1 ? '' : 's'}`}
          accent="emerald"
          icon={PercentSquare}
          testId="stat-cash-10"
        />
        <StatTile
          label="20% off"
          value={`$${friends20.amount?.toFixed(2) ?? '0.00'}`}
          sub={`${friends20.count} time${friends20.count === 1 ? '' : 's'}`}
          accent="amber"
          icon={PercentSquare}
          testId="stat-friends-20"
        />
        <StatTile
          label="Staff active"
          value={`${data?.by_bartender?.length || 0}`}
          sub="applied at least one"
          accent="zinc"
          icon={UsersIcon}
          testId="stat-staff-count"
        />
      </div>

      {/* Bartender leaderboard */}
      <Card className="bg-white/[0.03] border-white/10 rounded-sm p-4" data-testid="discount-by-bartender">
        <h3 className="text-sm font-serif text-white uppercase tracking-[0.18em] mb-3 flex items-center gap-2">
          <UsersIcon className="w-4 h-4 text-gold" /> Discounts by staff
        </h3>
        {!data?.by_bartender?.length ? (
          <p className="text-zinc-500 text-sm font-sans">No discounts applied in this range.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm font-sans">
              <thead>
                <tr className="text-zinc-500 text-[10px] uppercase tracking-[0.15em] border-b border-white/5">
                  <th className="text-left py-2 font-sans font-semibold">Staff</th>
                  <th className="text-left py-2 font-sans font-semibold">Role</th>
                  <th className="text-right py-2 font-sans font-semibold">Times</th>
                  <th className="text-right py-2 font-sans font-semibold">Total $ discounted</th>
                </tr>
              </thead>
              <tbody>
                {data.by_bartender.map(b => (
                  <tr key={b.user_id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`bartender-row-${b.user_id}`}>
                    <td className="py-2.5 text-white">{b.name}</td>
                    <td className="py-2.5 text-zinc-400 capitalize">{(b.role || '').replace('_', ' ') || '—'}</td>
                    <td className="py-2.5 text-right text-white font-mono">{b.count}</td>
                    <td className="py-2.5 text-right text-gold font-mono font-semibold">${b.amount.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Daily series */}
      {data?.by_day?.length > 0 && (
        <Card className="bg-white/[0.03] border-white/10 rounded-sm p-4" data-testid="discount-by-day">
          <h3 className="text-sm font-serif text-white uppercase tracking-[0.18em] mb-3 flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gold" /> Daily breakdown
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
            {data.by_day.map(d => (
              <div key={d.date} className="bg-white/[0.02] border border-white/5 rounded-sm p-2.5" data-testid={`day-row-${d.date}`}>
                <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-sans">{d.date}</p>
                <p className="text-gold font-mono text-base font-semibold mt-0.5">${d.amount.toFixed(2)}</p>
                <p className="text-zinc-500 text-[10px] font-sans">{d.count} order{d.count === 1 ? '' : 's'}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Recent events */}
      <Card className="bg-white/[0.03] border-white/10 rounded-sm p-4" data-testid="discount-recent">
        <h3 className="text-sm font-serif text-white uppercase tracking-[0.18em] mb-3">Recent discounts (last 50)</h3>
        {!data?.recent?.length ? (
          <p className="text-zinc-500 text-sm font-sans">Nothing to show yet.</p>
        ) : (
          <div className="space-y-1.5">
            {data.recent.map(r => (
              <div
                key={r.order_id}
                className="flex items-center justify-between gap-3 py-2 px-3 bg-white/[0.02] rounded-sm border border-white/5 hover:bg-white/[0.04] text-sm font-sans flex-wrap"
                data-testid={`recent-${r.order_id}`}
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className="text-gold font-serif text-sm font-semibold">T{r.table_number}</span>
                  <span className="text-white truncate">{r.customer_name || 'guest'}</span>
                  <Badge
                    className={`border-0 font-sans text-[10px] uppercase tracking-[0.12em] ${
                      r.discount_kind === 'friends_20'
                        ? 'bg-amber-500/15 text-amber-300'
                        : 'bg-emerald-500/15 text-emerald-300'
                    }`}
                  >
                    {r.discount_label}
                  </Badge>
                  <Badge
                    className={`border-0 font-sans text-[10px] uppercase ${
                      r.payment_status === 'paid'
                        ? 'bg-zinc-700/40 text-zinc-300'
                        : 'bg-zinc-800/40 text-zinc-500'
                    }`}
                  >
                    {r.payment_status}
                  </Badge>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-zinc-500">by <span className="text-zinc-300">{r.applied_by_name}</span></span>
                  <span className="text-red-300 font-mono">-${r.discount_amount.toFixed(2)}</span>
                  <span className="text-zinc-500 font-mono">
                    ${r.subtotal.toFixed(2)} → <span className="text-gold">${r.total.toFixed(2)}</span>
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

const PresetBtn = ({ label, onClick, testId }) => (
  <button
    onClick={onClick}
    className="h-9 px-3 rounded-sm bg-white/[0.04] border border-white/10 text-zinc-300 hover:bg-white/[0.08] hover:text-white font-sans text-[11px] uppercase tracking-[0.12em] font-semibold transition"
    data-testid={testId}
  >
    {label}
  </button>
);

const StatTile = ({ label, value, sub, accent, icon: Icon, testId }) => {
  const accentMap = {
    gold: 'text-gold',
    emerald: 'text-emerald-300',
    amber: 'text-amber-300',
    zinc: 'text-zinc-200',
  };
  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-sm p-4 flex items-start gap-3" data-testid={testId}>
      <Icon className={`w-5 h-5 mt-0.5 ${accentMap[accent] || 'text-white'}`} />
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-sans">{label}</p>
        <p className={`font-serif text-2xl font-semibold ${accentMap[accent] || 'text-white'}`}>{value}</p>
        <p className="text-zinc-500 text-[11px] font-sans mt-0.5">{sub}</p>
      </div>
    </div>
  );
};
